import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  useTheme,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import TimelineIcon from '@mui/icons-material/Timeline';
import FunctionsIcon from '@mui/icons-material/Functions';
import WavesIcon from '@mui/icons-material/Waves';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { tokens } from '../../theme';
import { CURVE_POINTS_REQUIRED, isCurveMode } from './coordinateUtils';

/**
 * Grid Controls Toolbar Component
 * Provides mode selection and action buttons for the coordinate grid
 */
const GridControls = ({
  mode,
  setMode,
  allowedModes = ['point', 'line', 'polygon', 'circle'],
  onUndo,
  onClear,
  onCancel,
  canUndo = false,
  hasElements = false,
  hasPending = false,
  compact = false,
  lineInputMode = 'twoPoints',
  setLineInputMode,
  pointInputMode = 'click',
  setPointInputMode,
  allowedPointInputMethods = ['click', 'type'],
  allowedLineInputMethods = ['twoPoints', 'equation'],
  curveInputMode = 'click',
  setCurveInputMode,
  allowedCurveInputMethods = ['click', 'type'],
  logBase = 10,
  setLogBase,
  customLogBase = '',
  setCustomLogBase,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const modeConfig = {
    point: {
      icon: <RadioButtonUncheckedIcon fontSize="small" />,
      label: 'Point',
      tooltip: 'Place points on the grid',
    },
    line: {
      icon: <ShowChartIcon fontSize="small" />,
      label: 'Line',
      tooltip: 'Draw lines between two points',
    },
    polygon: {
      icon: <ChangeHistoryIcon fontSize="small" />,
      label: 'Polygon',
      tooltip: 'Draw polygons (click points, then click first point to close)',
    },
    circle: {
      icon: <PanoramaFishEyeIcon fontSize="small" />,
      label: 'Circle',
      tooltip: 'Draw circles (click center, then click to set radius)',
    },
    quadratic: {
      icon: <TimelineIcon fontSize="small" />,
      label: 'Parabola',
      tooltip: `Draw parabola (click ${CURVE_POINTS_REQUIRED.quadratic} points)`,
    },
    absolute: {
      icon: <FunctionsIcon fontSize="small" />,
      label: 'Abs Value',
      tooltip: `Draw absolute value (click vertex, then ${CURVE_POINTS_REQUIRED.absolute - 1} more point)`,
    },
    sqrt: {
      icon: <TrendingUpIcon fontSize="small" />,
      label: 'Sqrt',
      tooltip: `Draw square root (click start point, then ${CURVE_POINTS_REQUIRED.sqrt - 1} more point)`,
    },
    nthRoot: {
      icon: <TrendingUpIcon fontSize="small" />,
      label: 'Nth Root',
      tooltip: `Draw nth root (click start point, then ${CURVE_POINTS_REQUIRED.nthRoot - 1} more point)`,
    },
    exponential: {
      icon: <TrendingUpIcon fontSize="small" />,
      label: 'Exp',
      tooltip: `Draw exponential (click ${CURVE_POINTS_REQUIRED.exponential} points)`,
    },
    logarithmic: {
      icon: <TrendingUpIcon fontSize="small" />,
      label: 'Log',
      tooltip: `Draw logarithmic (click ${CURVE_POINTS_REQUIRED.logarithmic} points)`,
    },
    sine: {
      icon: <WavesIcon fontSize="small" />,
      label: 'Sine',
      tooltip: `Draw sine wave (click max, min, then zero crossing)`,
    },
    cosine: {
      icon: <WavesIcon fontSize="small" />,
      label: 'Cosine',
      tooltip: `Draw cosine wave (click max, min, then zero crossing)`,
    },
    tangent: {
      icon: <WavesIcon fontSize="small" />,
      label: 'Tan',
      tooltip: `Draw tangent (click midline crossing, adjacent asymptote, one more point)`,
    },
    polynomial2: {
      icon: <TimelineIcon fontSize="small" />,
      label: 'Poly²',
      tooltip: 'Click 3 points to fit a degree-2 polynomial (y = ax² + bx + c)',
    },
    polynomial3: {
      icon: <TimelineIcon fontSize="small" />,
      label: 'Poly³',
      tooltip: 'Click 4 points to fit a degree-3 polynomial (cubic)',
    },
    polynomial4: {
      icon: <TimelineIcon fontSize="small" />,
      label: 'Poly⁴',
      tooltip: 'Click 5 points to fit a degree-4 polynomial (quartic)',
    },
    polynomial5: {
      icon: <TimelineIcon fontSize="small" />,
      label: 'Poly⁵',
      tooltip: 'Click 6 points to fit a degree-5 polynomial (quintic)',
    },
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      // Cancel any pending operation when changing modes
      if (hasPending && onCancel) {
        onCancel();
      }
      setMode(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: compact ? 1 : 1.5,
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      {/* Mode selector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!compact && (
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            Mode:
          </Typography>
        )}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            // Max width to ensure 5 buttons per row (5 * buttonWidth + 4 * gap)
            maxWidth: compact ? 200 : 400,
            '& .MuiToggleButton-root': {
              borderColor: colors.grey[600],
              color: colors.grey[300],
              // Remove default connected button styling for wrapped layout
              borderRadius: '4px !important',
              border: `1px solid ${colors.grey[600]} !important`,
              // Fixed width for consistent 5-per-row layout
              minWidth: compact ? 36 : 75,
              maxWidth: compact ? 36 : 75,
              flex: '0 0 auto',
              '&.Mui-selected': {
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.blueAccent[600],
                },
              },
              '&:hover': {
                backgroundColor: colors.primary[600],
              },
            },
          }}
        >
          {allowedModes.includes('point') && (
            <ToggleButton value="point">
              <Tooltip title={modeConfig.point.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.point.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.point.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('line') && (
            <ToggleButton value="line">
              <Tooltip title={modeConfig.line.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.line.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.line.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('polygon') && (
            <ToggleButton value="polygon">
              <Tooltip title={modeConfig.polygon.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.polygon.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.polygon.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('circle') && (
            <ToggleButton value="circle">
              <Tooltip title={modeConfig.circle.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.circle.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.circle.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('quadratic') && (
            <ToggleButton value="quadratic">
              <Tooltip title={modeConfig.quadratic.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.quadratic.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.quadratic.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('absolute') && (
            <ToggleButton value="absolute">
              <Tooltip title={modeConfig.absolute.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.absolute.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.absolute.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('sqrt') && (
            <ToggleButton value="sqrt">
              <Tooltip title={modeConfig.sqrt.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.sqrt.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.sqrt.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('nthRoot') && (
            <ToggleButton value="nthRoot">
              <Tooltip title={modeConfig.nthRoot.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.nthRoot.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.nthRoot.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('exponential') && (
            <ToggleButton value="exponential">
              <Tooltip title={modeConfig.exponential.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.exponential.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.exponential.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('logarithmic') && (
            <ToggleButton value="logarithmic">
              <Tooltip title={modeConfig.logarithmic.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.logarithmic.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.logarithmic.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('sine') && (
            <ToggleButton value="sine">
              <Tooltip title={modeConfig.sine.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.sine.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.sine.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('cosine') && (
            <ToggleButton value="cosine">
              <Tooltip title={modeConfig.cosine.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.cosine.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.cosine.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('tangent') && (
            <ToggleButton value="tangent">
              <Tooltip title={modeConfig.tangent.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.tangent.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.tangent.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('polynomial2') && (
            <ToggleButton value="polynomial2">
              <Tooltip title={modeConfig.polynomial2.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.polynomial2.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.polynomial2.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('polynomial3') && (
            <ToggleButton value="polynomial3">
              <Tooltip title={modeConfig.polynomial3.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.polynomial3.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.polynomial3.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('polynomial4') && (
            <ToggleButton value="polynomial4">
              <Tooltip title={modeConfig.polynomial4.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.polynomial4.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.polynomial4.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('polynomial5') && (
            <ToggleButton value="polynomial5">
              <Tooltip title={modeConfig.polynomial5.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {modeConfig.polynomial5.icon}
                  {!compact && (
                    <Typography variant="caption">{modeConfig.polynomial5.label}</Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        {/* Line sub-mode toggle - only show when multiple methods allowed */}
        {mode === 'line' &&
          allowedModes.includes('line') &&
          setLineInputMode &&
          allowedLineInputMethods.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Input:
              </Typography>
              <ToggleButtonGroup
                value={lineInputMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    if (hasPending && onCancel) {
                      onCancel();
                    }
                    setLineInputMode(newMode);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderColor: colors.grey[600],
                    color: colors.grey[300],
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                    '&.Mui-selected': {
                      backgroundColor: colors.greenAccent[700],
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: colors.greenAccent[600],
                      },
                    },
                    '&:hover': {
                      backgroundColor: colors.primary[600],
                    },
                  },
                }}
              >
                {allowedLineInputMethods.includes('twoPoints') && (
                  <ToggleButton value="twoPoints">
                    <Tooltip title="Click two points to draw a line">
                      <Typography variant="caption">Two Points</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
                {allowedLineInputMethods.includes('equation') && (
                  <ToggleButton value="equation">
                    <Tooltip title="Enter an equation like y=2x+1">
                      <Typography variant="caption">Equation</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>
          )}

        {/* Point sub-mode toggle - only show when multiple methods allowed */}
        {mode === 'point' &&
          allowedModes.includes('point') &&
          setPointInputMode &&
          allowedPointInputMethods.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Input:
              </Typography>
              <ToggleButtonGroup
                value={pointInputMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setPointInputMode(newMode);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderColor: colors.grey[600],
                    color: colors.grey[300],
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                    '&.Mui-selected': {
                      backgroundColor: colors.greenAccent[700],
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: colors.greenAccent[600],
                      },
                    },
                    '&:hover': {
                      backgroundColor: colors.primary[600],
                    },
                  },
                }}
              >
                {allowedPointInputMethods.includes('click') && (
                  <ToggleButton value="click">
                    <Tooltip title="Click on the grid to place points">
                      <Typography variant="caption">Click</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
                {allowedPointInputMethods.includes('type') && (
                  <ToggleButton value="type">
                    <Tooltip title="Type coordinates like (1/2, 3/4)">
                      <Typography variant="caption">Type</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>
          )}

        {/* Curve sub-mode toggle - only show when in curve mode and multiple methods allowed */}
        {isCurveMode(mode) &&
          allowedModes.some(m => isCurveMode(m)) &&
          setCurveInputMode &&
          allowedCurveInputMethods.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Input:
              </Typography>
              <ToggleButtonGroup
                value={curveInputMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    if (hasPending && onCancel) {
                      onCancel();
                    }
                    setCurveInputMode(newMode);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderColor: colors.grey[600],
                    color: colors.grey[300],
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                    '&.Mui-selected': {
                      backgroundColor: colors.greenAccent[700],
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: colors.greenAccent[600],
                      },
                    },
                    '&:hover': {
                      backgroundColor: colors.primary[600],
                    },
                  },
                }}
              >
                {allowedCurveInputMethods.includes('click') && (
                  <ToggleButton value="click">
                    <Tooltip title="Click points on the grid to fit a curve">
                      <Typography variant="caption">Click</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
                {allowedCurveInputMethods.includes('type') && (
                  <ToggleButton value="type">
                    <Tooltip title="Type coordinates like (1/2, 3/4)">
                      <Typography variant="caption">Type</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
                {allowedCurveInputMethods.includes('equation') && (
                  <ToggleButton value="equation">
                    <Tooltip title="Enter an equation like y=(1/2)^x">
                      <Typography variant="caption">Equation</Typography>
                    </Tooltip>
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>
          )}

        {/* Log base selector - only show when in logarithmic mode */}
        {mode === 'logarithmic' && setLogBase && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Base:
            </Typography>
            <ToggleButtonGroup
              value={
                logBase === 2 ? '2' :
                logBase === 5 ? '5' :
                logBase === 10 ? '10' :
                Math.abs(logBase - Math.E) < 1e-10 ? 'e' : null
              }
              exclusive
              onChange={(e, newBase) => {
                if (newBase !== null) {
                  if (hasPending && onCancel) {
                    onCancel();
                  }
                  const numBase = newBase === 'e' ? Math.E : parseFloat(newBase);
                  setLogBase(numBase);
                  if (setCustomLogBase) setCustomLogBase('');
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  borderColor: colors.grey[600],
                  color: colors.grey[300],
                  fontSize: '0.7rem',
                  padding: '4px 8px',
                  minWidth: '32px',
                  '&.Mui-selected': {
                    backgroundColor: colors.greenAccent[700],
                    color: colors.grey[100],
                    '&:hover': {
                      backgroundColor: colors.greenAccent[600],
                    },
                  },
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                  },
                },
              }}
            >
              <ToggleButton value="2">
                <Typography variant="caption">2</Typography>
              </ToggleButton>
              <ToggleButton value="e">
                <Typography variant="caption">e</Typography>
              </ToggleButton>
              <ToggleButton value="5">
                <Typography variant="caption">5</Typography>
              </ToggleButton>
              <ToggleButton value="10">
                <Typography variant="caption">10</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Other"
              value={customLogBase}
              onChange={(e) => {
                if (setCustomLogBase) setCustomLogBase(e.target.value);
                const parts = e.target.value.split('/');
                const val = parts.length === 2
                  ? parseFloat(parts[0]) / parseFloat(parts[1])
                  : parseFloat(e.target.value);
                if (!isNaN(val) && val > 0 && val !== 1 && val < 10000) {
                  if (hasPending && onCancel) {
                    onCancel();
                  }
                  setLogBase(val);
                }
              }}
              sx={{
                width: 60,
                '& .MuiInputBase-root': {
                  height: 28,
                  fontSize: '0.7rem',
                  color: colors.grey[300],
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Cancel button - only show when there's a pending operation */}
        {hasPending && onCancel && (
          <Tooltip title="Cancel current operation (Esc)">
            <IconButton
              onClick={onCancel}
              size="small"
              sx={{
                color: colors.grey[300],
                '&:hover': {
                  color: colors.redAccent[400],
                  backgroundColor: colors.primary[600],
                },
              }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Undo button */}
        <Tooltip title="Undo (Ctrl+Z)">
          <span>
            <IconButton
              onClick={onUndo}
              disabled={!canUndo}
              size="small"
              sx={{
                color: colors.grey[300],
                '&:hover': {
                  color: colors.grey[100],
                  backgroundColor: colors.primary[600],
                },
                '&.Mui-disabled': {
                  color: colors.grey[600],
                },
              }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Clear button */}
        <Tooltip title="Clear all">
          <span>
            <IconButton
              onClick={onClear}
              disabled={!hasElements && !hasPending}
              size="small"
              sx={{
                color: colors.grey[300],
                '&:hover': {
                  color: colors.redAccent[400],
                  backgroundColor: colors.primary[600],
                },
                '&.Mui-disabled': {
                  color: colors.grey[600],
                },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default GridControls;
